import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Grid, Typography, Button, TextField,
  MenuItem, Table, TableHead, TableBody, TableRow, TableCell,
  Paper, Chip, IconButton, Tooltip, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Avatar,
  LinearProgress, Divider, Tabs, Tab, InputAdornment, Snackbar,
} from "@mui/material";
import {
  Add, Search, Visibility, CheckCircle, CloudUpload, Delete,
  Description, AssignmentInd, VerifiedUser, AccountBalance,
  Laptop, People, Close as CloseIcon, Save, Refresh,
  FolderOpen, Warning,
} from "@mui/icons-material";
import { ThemeProvider } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import recruiterTheme, {
  FONT_BODY, FONT_DISPLAY, TYPE, RADIUS, SHADOW, PAGE_PADDING, COLORS, CONTROL_HEIGHT, ELLIPSIS, humanizeStatus,
} from "../../../styles/recruiterTheme";

// ── Design tokens ─────────────────────────────────────────────────────────────
const PURPLE_DEEP  = "#2D1B5E";
const PURPLE_MID   = "#4F378B";
const PURPLE_BRAND = "#6750A4";
const PURPLE_LIGHT = "#EDE7F6";
const PURPLE_SOFT  = "#F3EEF9";
const PURPLE_MUTED = "#8B7DB5";
const PURPLE_PALE  = "#B0A0D0";

const BASE           = process.env.REACT_APP_API_BASE_URL;
const EMPLOYEE_BASE  = process.env.REACT_APP_API_EMPLOYEES_URL;
const ONBOARDING_BASE = process.env.REACT_APP_API_ONBOARDING_URL;

// utils/apiInterceptor.js adds the Bearer token to every fetch.
const authHeaders = () => ({ "Content-Type": "application/json" });

const handle = async (res) => {
  let data = {};
  try { data = await res.json(); } catch { /* empty / non-JSON body */ }
  if (!res.ok || data?.success === false) {
    throw new Error(data?.message || data?.detail || `Request failed (${res.status})`);
  }
  return data;
};

const getEmployees     = ()           => fetch(`${EMPLOYEE_BASE}/`,                        { headers: authHeaders() }).then(handle);
const getOnboarding    = (eid)        => fetch(`${ONBOARDING_BASE}/${eid}`,                 { headers: authHeaders() }).then(handle);
const updateOnboarding = (eid, pl)    => fetch(`${ONBOARDING_BASE}/${eid}`,                 { method: "PUT",    headers: authHeaders(), body: JSON.stringify(pl) }).then(handle);
const updateChecklist  = (eid, i, pl) => fetch(`${ONBOARDING_BASE}/${eid}/checklist/${i}`,  { method: "PUT",    headers: authHeaders(), body: JSON.stringify(pl) }).then(handle);
const addDocument      = (eid, pl)    => fetch(`${ONBOARDING_BASE}/${eid}/document`,        { method: "POST",   headers: authHeaders(), body: JSON.stringify(pl) }).then(handle);
const updateDocument   = (eid, i, pl) => fetch(`${ONBOARDING_BASE}/${eid}/document/${i}`,   { method: "PUT",    headers: authHeaders(), body: JSON.stringify(pl) }).then(handle);
const deleteDocument   = (eid, i)     => fetch(`${ONBOARDING_BASE}/${eid}/document/${i}`,   { method: "DELETE", headers: authHeaders() }).then(handle);

const uploadDocFile = async (eid, idx, file) => {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${ONBOARDING_BASE}/${eid}/document/${idx}/upload`, {
    method: "POST",
    body: fd,
  });
  return handle(res);
};

// ── Constants ──────────────────────────────────────────────────────────────────
const DOC_STATUSES  = ["Pending", "Received", "Verified", "Waived"];
const BGV_STATUSES  = ["Not Initiated", "Initiated", "In Progress", "Completed", "Failed"];
const ACCOUNT_TYPES = ["Savings", "Current", "NRE", "NRO"];

const DOC_STATUS_COLOR = {
  Pending:  { bg: "#fff8e1", color: "#e65100", border: "#ffe082" },
  Received: { bg: "#ede9fe", color: PURPLE_MID, border: "#c4b5fd" },
  Verified: { bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7" },
  Waived:   { bg: PURPLE_SOFT, color: PURPLE_BRAND, border: PURPLE_LIGHT },
};

const BGV_COLOR = {
  "Not Initiated": { bg: "#f8f4ff", color: PURPLE_MUTED },
  "Initiated":     { bg: PURPLE_SOFT, color: PURPLE_BRAND },
  "In Progress":   { bg: "#fef9c3", color: "#854d0e" },
  "Completed":     { bg: "#dcfce7", color: "#166534" },
  "Failed":        { bg: "#fee2e2", color: "#991b1b" },
};

const DOC_CATEGORIES = {
  Identity:     ["Aadhar Card", "PAN Card", "Passport", "Voter ID", "Driving License"],
  Address:      ["Utility Bill", "Rental Agreement", "Bank Statement (Address Proof)"],
  Education:    ["10th Marksheet", "12th Marksheet", "Graduation Certificate", "PG Certificate", "Professional Certifications"],
  Professional: ["Previous Offer Letter", "Relieving Letter", "Experience Letter", "Last 3 Months Payslips", "Form 16"],
  Medical:      ["Medical Fitness Certificate", "Health Declaration Form"],
  Other:        ["Photograph", "Signed Offer Letter", "Signed NDA", "Signed Employment Agreement"],
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

// ── Shared field style ─────────────────────────────────────────────────────────
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: `${RADIUS.sm}px`,
    fontFamily: FONT_BODY,
    fontSize: TYPE.body,
    "&:hover fieldset": { borderColor: PURPLE_BRAND },
    "&.Mui-focused fieldset": { borderColor: PURPLE_BRAND, borderWidth: 2 },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: PURPLE_BRAND },
};

// ── Shared helpers ────────────────────────────────────────────────────────────
function SectionHeader({ title, subtitle }) {
  return (
    <Box mb={2.5}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <Box sx={{
          width: 4, height: 20, borderRadius: 2,
          background: "linear-gradient(135deg, #6750A4, #4F378B)",
        }} />
        <Typography sx={{
          fontFamily: FONT_BODY,
          fontWeight: 700, fontSize: TYPE.cardTitle, color: PURPLE_DEEP,
        }}>
          {title}
        </Typography>
      </Box>
      {subtitle && (
        <Typography sx={{
          fontFamily: FONT_BODY,
          fontSize: TYPE.caption, color: PURPLE_MUTED, mt: 0.5, pl: 1.5,
        }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

// Primary button look shared by the save bars and dialogs.
const primaryBtnSx = {
  height: CONTROL_HEIGHT.md,
  background: "linear-gradient(135deg, #6750A4, #4F378B)",
  "&:hover": { background: "linear-gradient(135deg, #4F378B, #2D1B5E)" },
  fontFamily: FONT_BODY, fontWeight: 600, borderRadius: `${RADIUS.sm}px`,
  boxShadow: "0 4px 14px rgba(79,55,139,0.3)", textTransform: "none",
};

function SaveBar({ onSave, saving, success, error, onDismissError }) {
  return (
    <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1.5} mt={3}
      pt={2} sx={{ borderTop: `1px solid ${PURPLE_LIGHT}` }}>
      {error && (
        <Alert severity="error" onClose={onDismissError} sx={{ flex: 1, py: 0, borderRadius: `${RADIUS.sm}px` }}>{error}</Alert>
      )}
      {success && (
        <Chip label="Saved ✓" size="small" sx={{ bgcolor: "#dcfce7", color: "#166534", fontWeight: 700 }} />
      )}
      <Button
        variant="contained"
        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
        onClick={onSave} disabled={saving}
        sx={{ ...primaryBtnSx, flexShrink: 0 }}
      >
        {saving ? "Saving…" : "Save Changes"}
      </Button>
    </Box>
  );
}

// ── TAB 0 — OVERVIEW ──────────────────────────────────────────────────────────
function OverviewTab({ employeeId, data, employee, onRefresh, onSaveSuccess }) {
  const [form,    setForm]    = useState({});
  const [saving,  setSaving]  = useState(false);
  const [success, setSuccess] = useState(false);
  const [error,   setError]   = useState("");

  useEffect(() => {
    setForm({
      blood_group:        data?.blood_group        || "",
      personal_email:     data?.personal_email     || "",
      referred_by:        data?.referred_by        || "",
      probation_end_date: data?.probation_end_date ? data.probation_end_date.split("T")[0] : "",
      hr_notes:           data?.hr_notes           || "",
      it_notes:           data?.it_notes           || "",
    });
  }, [data]);

  const handleChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    if (saving) return;
    setSaving(true); setSuccess(false); setError("");
    try {
      await updateOnboarding(employeeId, form);
      setSuccess(true); onRefresh();
      onSaveSuccess?.("Overview details saved successfully");
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) { setError(err?.message || "Failed to save overview details"); }
    finally { setSaving(false); }
  };

  const LabelValue = ({ label, value }) => (
    <Box>
      <Typography sx={{
        fontFamily: FONT_BODY,
        fontSize: TYPE.overline, color: PURPLE_MUTED, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontFamily: FONT_BODY,
        fontSize: TYPE.body, fontWeight: 700, color: PURPLE_DEEP,
      }}>
        {value || "—"}
      </Typography>
    </Box>
  );

  return (
    <Box>
      <SectionHeader title="Personal & Joining Details" subtitle="Basic onboarding information for this employee" />
      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={12} sm={6} md={4}><LabelValue label="Employee ID" value={employee?.emp_id} /></Grid>
        <Grid item xs={12} sm={6} md={4}><LabelValue label="Name" value={employee?.name} /></Grid>
        <Grid item xs={12} sm={6} md={4}><LabelValue label="Joining Date" value={fmtDate(data?.joining_date || employee?.date_of_joining)} /></Grid>
      </Grid>
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} md={4}><TextField fullWidth sx={fieldSx} size="small" label="Blood Group" name="blood_group" value={form.blood_group || ""} onChange={handleChange} placeholder="e.g. O+" /></Grid>
        <Grid item xs={12} sm={6} md={4}><TextField fullWidth sx={fieldSx} size="small" label="Personal Email" name="personal_email" type="email" value={form.personal_email || ""} onChange={handleChange} /></Grid>
        <Grid item xs={12} sm={6} md={4}><TextField fullWidth sx={fieldSx} size="small" label="Referred By" name="referred_by" value={form.referred_by || ""} onChange={handleChange} /></Grid>
        <Grid item xs={12} sm={6} md={4}><TextField fullWidth sx={fieldSx} size="small" label="Probation End Date" name="probation_end_date" type="date" value={form.probation_end_date || ""} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
        <Grid item xs={12} sm={6}><TextField fullWidth sx={fieldSx} multiline rows={3} size="small" label="HR Notes" name="hr_notes" value={form.hr_notes || ""} onChange={handleChange} placeholder="Internal HR notes…" /></Grid>
        <Grid item xs={12} sm={6}><TextField fullWidth sx={fieldSx} multiline rows={3} size="small" label="IT Notes" name="it_notes" value={form.it_notes || ""} onChange={handleChange} placeholder="IT setup notes…" /></Grid>
      </Grid>
      <SaveBar onSave={handleSave} saving={saving} success={success} error={error} onDismissError={() => setError("")} />
    </Box>
  );
}

// ── TAB 1 — CHECKLIST ─────────────────────────────────────────────────────────
function ChecklistTab({ employeeId, data, onRefresh, onSaveSuccess }) {
  const [items,  setItems]  = useState([]);
  const [saving, setSaving] = useState(null);
  const [error,  setError]  = useState("");

  useEffect(() => {
    if (data?.checklist) setItems([...data.checklist]);
  }, [data]);

  const doneCount = items.filter(i => i.done).length;
  const pct       = items.length ? Math.round((doneCount / items.length) * 100) : 0;

  const handleToggle = async (idx) => {
    if (saving !== null) return;
    const prev = items;
    const next = items.map((item, i) => i === idx ? { ...item, done: !item.done } : item);
    setItems(next); setSaving(idx); setError("");
    try {
      await updateChecklist(employeeId, idx, { done: next[idx].done, remarks: next[idx].remarks || "" });
      onSaveSuccess?.("Checklist updated"); onRefresh();
    } catch (err) {
      setItems(prev);
      setError(err?.message || "Failed to update checklist item");
    }
    finally { setSaving(null); }
  };

  const handleRemarks = (idx, val) =>
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, remarks: val } : item));

  const saveRemarks = async (idx) => {
    setSaving(idx); setError("");
    try {
      await updateChecklist(employeeId, idx, { done: items[idx].done, remarks: items[idx].remarks || "" });
      onSaveSuccess?.("Checklist updated"); onRefresh();
    } catch (err) { setError(err?.message || "Failed to save remarks"); }
    finally { setSaving(null); }
  };

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2, borderRadius: `${RADIUS.sm}px` }}>{error}</Alert>}
      {/* Progress */}
      <Box mb={3} p={2.5} sx={{
        bgcolor: PURPLE_SOFT, borderRadius: "12px",
        border: `1px solid ${PURPLE_LIGHT}`,
      }}>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, color: PURPLE_DEEP }}>
            Onboarding Progress
          </Typography>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, color: pct === 100 ? "#166534" : PURPLE_BRAND }}>
            {doneCount} / {items.length} completed
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={pct} sx={{
          height: 8, borderRadius: 4, bgcolor: PURPLE_LIGHT,
          "& .MuiLinearProgress-bar": {
            background: pct === 100 ? "#16a34a" : "linear-gradient(90deg, #6750A4, #4F378B)",
            borderRadius: 4,
          },
        }} />
        {pct === 100 && (
          <Box display="flex" alignItems="center" gap={0.8} mt={1}>
            <CheckCircle sx={{ fontSize: 16, color: "#16a34a" }} />
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12, color: "#166534", fontWeight: 600 }}>
              All checklist items completed!
            </Typography>
          </Box>
        )}
      </Box>

      <Box display="flex" flexDirection="column" gap={1}>
        {items.map((item, idx) => (
          <Box key={idx} sx={{
            p: 1.8, borderRadius: `${RADIUS.sm}px`,
            border: `1px solid ${item.done ? "#bbf7d0" : PURPLE_LIGHT}`,
            bgcolor: item.done ? "#f0fdf4" : "#fff",
            transition: "all 0.15s",
          }}>
            <Box display="flex" alignItems="flex-start" gap={1.5}>
              <Box role="checkbox" aria-checked={!!item.done} onClick={() => handleToggle(idx)} sx={{
                width: 22, height: 22, borderRadius: "6px", flexShrink: 0,
                border: `2px solid ${item.done ? "#16a34a" : PURPLE_LIGHT}`,
                bgcolor: item.done ? "#16a34a" : "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", mt: 0.2, transition: "all 0.15s",
              }}>
                {saving === idx
                  ? <CircularProgress size={12} sx={{ color: item.done ? "#fff" : PURPLE_BRAND }} />
                  : item.done && <CheckCircle sx={{ fontSize: 14, color: "#fff" }} />}
              </Box>
              <Box flex={1}>
                <Typography sx={{
                  fontFamily: FONT_BODY,
                  fontSize: 13, fontWeight: 600,
                  color: item.done ? "#166534" : PURPLE_DEEP,
                  textDecoration: item.done ? "line-through" : "none", mb: 0.5,
                }}>
                  {item.label}
                </Typography>
                <TextField
                  size="small" placeholder="Add remarks…"
                  value={item.remarks || ""}
                  onChange={e => handleRemarks(idx, e.target.value)}
                  onBlur={() => {
                    if ((item.remarks || "") !== (data?.checklist?.[idx]?.remarks || "")) saveRemarks(idx);
                  }}
                  sx={{
                    width: "100%", ...fieldSx,
                    "& .MuiInputBase-input": { fontSize: 12, py: 0.6 },
                  }}
                />
              </Box>
              {item.done && (
                <Chip label="Done" size="small" sx={{
                  bgcolor: "#dcfce7", color: "#166534",
                  fontFamily: FONT_BODY,
                  fontSize: TYPE.overline, fontWeight: 700, height: 20,
                }} />
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── TAB 2 — DOCUMENTS ─────────────────────────────────────────────────────────
function DocumentsTab({ employeeId, data, onRefresh, onSaveSuccess }) {
  const [addOpen,   setAddOpen]   = useState(false);
  const [addForm,   setAddForm]   = useState({ name: "", category: "Identity", status: "Pending", remarks: "", file: null });
  const [addSaving, setAddSaving] = useState(false);
  const [uploadIdx, setUploadIdx] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState("");
  const fileRefs  = useRef({});
  const [addFile, setAddFile]     = useState(null);
  const addFileRef = useRef(null);
  const docs = data?.documents || [];

  const resetAddForm = () => {
    setAddForm({ name: "", category: "Identity", status: "Pending", remarks: "", file: null });
    setAddFile(null);
  };

  const handleAddDoc = async () => {
    if (addSaving) return;
    if (!addForm.name) { setError("Document name is required"); return; }
    setAddSaving(true); setError("");
    let res;
    try {
      res = await addDocument(employeeId, { name: addForm.name, category: addForm.category, status: addForm.status, remarks: addForm.remarks });
    } catch (err) {
      setError(err?.message || "Failed to add document");
      setAddSaving(false);
      return;
    }
    // The document now exists: close the dialog even if the file upload fails,
    // so a retry does not create a duplicate entry.
    let uploadError = "";
    if (addFile) {
      const newIdx = (res?.data?.documents?.length ?? docs.length + 1) - 1;
      try { await uploadDocFile(employeeId, newIdx, addFile); }
      catch (err) { uploadError = err?.message || "upload failed"; }
    }
    setAddOpen(false);
    resetAddForm();
    setAddSaving(false);
    if (uploadError) setError(`Document added, but the file could not be uploaded: ${uploadError}. Use the upload button on its row to retry.`);
    else onSaveSuccess?.("Document added successfully");
    onRefresh();
  };

  const handleStatusChange = async (idx, status) => {
    setError("");
    try { await updateDocument(employeeId, idx, { status }); onSaveSuccess?.("Document status updated"); onRefresh(); }
    catch (err) { setError(err?.message || "Failed to update document status"); }
  };

  const handleRemarksBlur = async (idx, remarks) => {
    setError("");
    try { await updateDocument(employeeId, idx, { remarks }); onRefresh(); }
    catch (err) { setError(err?.message || "Failed to save remarks"); }
  };

  const handleDelete = async (idx) => {
    if (!window.confirm("Delete this document?")) return;
    setError("");
    try { await deleteDocument(employeeId, idx); onSaveSuccess?.("Document deleted"); onRefresh(); }
    catch (err) { setError(err?.message || "Failed to delete document"); }
  };

  const handleFileUpload = async (idx, file) => {
    if (uploading) return;
    setUploadIdx(idx); setUploading(true); setError("");
    try { await uploadDocFile(employeeId, idx, file); onSaveSuccess?.("File uploaded"); onRefresh(); }
    catch (err) { setError(err?.message || "Upload failed"); }
    finally { setUploading(false); setUploadIdx(null); }
  };

  const handleViewFile = async (idx) => {
    setError("");
    try {
      const res  = await fetch(`${BASE}/onboarding/${employeeId}/document/${idx}/file`);
      if (!res.ok) throw new Error("File not found");
      const url = URL.createObjectURL(await res.blob());
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { setError(err?.message || "Could not open file"); }
  };

  const grouped = {};
  docs.forEach((doc, idx) => {
    const cat = doc.category || "Other";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push({ ...doc, _idx: idx });
  });

  const pendingCount = docs.filter(d => d.status === "Pending").length;

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 4, height: 20, borderRadius: 2, background: "linear-gradient(135deg, #6750A4, #4F378B)" }} />
            <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.cardTitle, color: PURPLE_DEEP }}>Documents</Typography>
          </Box>
          {pendingCount > 0 && (
            <Box display="flex" alignItems="center" gap={0.5} mt={0.3} pl={1.5}>
              <Warning sx={{ fontSize: 14, color: "#d97706" }} />
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12, color: "#d97706" }}>
                {pendingCount} document{pendingCount > 1 ? "s" : ""} still pending
              </Typography>
            </Box>
          )}
        </Box>
        <Button variant="contained" size="small" startIcon={<Add />}
          onClick={() => { setAddOpen(true); setError(""); }}
          sx={{ ...primaryBtnSx, height: CONTROL_HEIGHT.sm }}>
          Add Document
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: `${RADIUS.sm}px` }} onClose={() => setError("")}>{error}</Alert>}

      {docs.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={2}>
          <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: PURPLE_SOFT, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FolderOpen sx={{ fontSize: 32, color: PURPLE_PALE }} />
          </Box>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, color: PURPLE_MUTED }}>No documents added yet</Typography>
          <Button variant="outlined" startIcon={<Add />} onClick={() => { setAddOpen(true); setError(""); }}
            sx={{ height: CONTROL_HEIGHT.md, fontWeight: 600, borderColor: PURPLE_LIGHT, color: PURPLE_BRAND, borderRadius: `${RADIUS.sm}px`, textTransform: "none", fontFamily: FONT_BODY, "&:hover": { borderColor: PURPLE_BRAND, bgcolor: PURPLE_SOFT } }}>
            Add First Document
          </Button>
        </Box>
      ) : (
        Object.entries(grouped).map(([category, catDocs]) => (
          <Box key={category} mb={3}>
            <Typography sx={{
              fontFamily: FONT_BODY,
              fontSize: 11, fontWeight: 700, color: PURPLE_MUTED,
              textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.2,
              display: "flex", alignItems: "center", gap: 0.5,
            }}>
              {category}
              <Chip label={catDocs.length} size="small" sx={{ height: 16, fontSize: TYPE.overline, bgcolor: PURPLE_SOFT, color: PURPLE_MUTED }} />
            </Typography>
            <Paper variant="outlined" sx={{ borderRadius: `${RADIUS.sm}px`, overflow: "hidden", borderColor: PURPLE_LIGHT }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: PURPLE_SOFT }}>
                    {["Document", "Status", "Remarks", "File", "Actions"].map(h => (
                      <TableCell key={h} sx={{ fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: PURPLE_MUTED, textTransform: "uppercase", letterSpacing: "0.08em", py: 1, whiteSpace: "nowrap" }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {catDocs.map((doc) => {
                    const sc = DOC_STATUS_COLOR[doc.status] || DOC_STATUS_COLOR.Pending;
                    return (
                      <TableRow key={`${doc._idx}-${doc.name}`} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                        <TableCell title={doc.name} sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, fontWeight: 600, color: PURPLE_DEEP, py: 1, maxWidth: 200, ...ELLIPSIS }}>{doc.name}</TableCell>
                        <TableCell sx={{ py: 1.2 }}>
                          <TextField select size="small" value={DOC_STATUSES.includes(doc.status) ? doc.status : "Pending"}
                            onChange={e => handleStatusChange(doc._idx, e.target.value)}
                            sx={{ minWidth: 112, "& .MuiInputBase-input": { fontSize: TYPE.caption, fontWeight: 600, py: 0.5 }, "& .MuiOutlinedInput-root": { bgcolor: sc.bg, color: sc.color, borderRadius: `${RADIUS.sm}px`, "& fieldset": { borderColor: sc.border } } }}>
                            {DOC_STATUSES.map(s => <MenuItem key={s} value={s} sx={{ fontSize: TYPE.small }}>{s}</MenuItem>)}
                          </TextField>
                        </TableCell>
                        <TableCell sx={{ py: 1.2 }}>
                          <TextField size="small" key={`${doc._idx}:${doc.remarks || ""}`} defaultValue={doc.remarks || ""} placeholder="Add remarks…"
                            onBlur={e => e.target.value !== (doc.remarks || "") && handleRemarksBlur(doc._idx, e.target.value)}
                            sx={{ minWidth: 160, ...fieldSx, "& .MuiInputBase-input": { fontSize: TYPE.caption, py: 0.5 } }} />
                        </TableCell>
                        <TableCell sx={{ py: 1.2 }}>
                          {doc.file_name ? (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Tooltip title={doc.file_name}>
                                <Chip icon={<Description sx={{ fontSize: 12 }} />}
                                  label={doc.file_name}
                                  size="small" sx={{ bgcolor: PURPLE_SOFT, color: PURPLE_BRAND, maxWidth: 160 }} />
                              </Tooltip>
                              <Tooltip title="View file">
                                <IconButton size="small" onClick={() => handleViewFile(doc._idx)}>
                                  <Visibility sx={{ fontSize: 14, color: PURPLE_BRAND }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: PURPLE_PALE, whiteSpace: "nowrap" }}>No file</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1.2 }}>
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="Upload file">
                              <span>
                                <IconButton size="small" onClick={() => fileRefs.current[doc._idx]?.click()}
                                  disabled={uploading}
                                  sx={{ color: PURPLE_BRAND, bgcolor: PURPLE_SOFT, borderRadius: "8px", "&:hover": { bgcolor: PURPLE_LIGHT } }}>
                                  {uploading && uploadIdx === doc._idx ? <CircularProgress size={14} /> : <CloudUpload sx={{ fontSize: 16 }} />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Delete document">
                              <IconButton size="small" color="error" onClick={() => handleDelete(doc._idx)}
                                sx={{ bgcolor: "#fef2f2", borderRadius: "8px", "&:hover": { bgcolor: "#fee2e2" } }}>
                                <Delete sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <input type="file" hidden ref={el => fileRefs.current[doc._idx] = el}
                              onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(doc._idx, f); e.target.value = ""; }} />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        ))
      )}

      {/* Add Document Dialog */}
      <Dialog open={addOpen} onClose={() => { if (!addSaving) setAddOpen(false); }} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: `${RADIUS.lg}px` } }}>
        <DialogTitle sx={{
          color: PURPLE_DEEP,
          borderBottom: `1px solid ${PURPLE_LIGHT}`,
          borderTop: `4px solid ${PURPLE_BRAND}`,
        }}>
          Add Document
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: `${RADIUS.sm}px` }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField select fullWidth sx={{ ...fieldSx, mt: 1 }} size="small" label="Category *"
                value={addForm.category} onChange={e => setAddForm(p => ({ ...p, category: e.target.value, name: "" }))}>
                {Object.keys(DOC_CATEGORIES).map(c => <MenuItem key={c} value={c} >{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth sx={{ ...fieldSx, mt: 1 }} size="small" label="Document Name *"
                value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))}>
                <MenuItem value="">— Select document —</MenuItem>
                {(DOC_CATEGORIES[addForm.category] || []).map(d => <MenuItem key={d} value={d} >{d}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth sx={{ ...fieldSx, mt: 1 }} size="small" label="Initial Status"
                value={addForm.status} onChange={e => setAddForm(p => ({ ...p, status: e.target.value }))}>
                {DOC_STATUSES.map(s => <MenuItem key={s} value={s} >{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth sx={{ ...fieldSx, mt: 1 }} size="small" multiline rows={2} label="Remarks"
                value={addForm.remarks} onChange={e => setAddForm(p => ({ ...p, remarks: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12, color: PURPLE_MUTED, fontWeight: 600, mb: 0.8 }}>
                Attach File <Typography component="span" sx={{ fontSize: 11, color: PURPLE_PALE, fontWeight: 400 }}>(optional)</Typography>
              </Typography>
              <Box onClick={() => addFileRef.current?.click()} sx={{
                border: `2px dashed ${addFile ? PURPLE_BRAND : PURPLE_LIGHT}`,
                borderRadius: `${RADIUS.sm}px`, p: 2, textAlign: "center",
                cursor: "pointer", bgcolor: addFile ? PURPLE_SOFT : "#fdfbff",
                transition: "all 0.15s",
                "&:hover": { borderColor: PURPLE_BRAND, bgcolor: PURPLE_SOFT },
              }}>
                {addFile ? (
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <Description sx={{ fontSize: 18, color: PURPLE_BRAND }} />
                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12, color: PURPLE_BRAND, fontWeight: 600 }}>{addFile.name}</Typography>
                    <IconButton size="small" onClick={e => { e.stopPropagation(); setAddFile(null); }} sx={{ color: "#dc2626", p: 0.3 }}>
                      <Delete sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <CloudUpload sx={{ fontSize: 20, color: PURPLE_PALE }} />
                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12, color: PURPLE_MUTED }}>Click to attach a file</Typography>
                  </Box>
                )}
              </Box>
              <input type="file" hidden ref={addFileRef}
                onChange={e => { const f = e.target.files?.[0]; if (f) setAddFile(f); e.target.value = ""; }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, borderTop: `1px solid ${PURPLE_LIGHT}`, gap: 1 }}>
          <Button onClick={() => setAddOpen(false)} disabled={addSaving}
            sx={{ height: CONTROL_HEIGHT.md, fontWeight: 600, color: PURPLE_MUTED, textTransform: "none", fontFamily: FONT_BODY, borderRadius: `${RADIUS.sm}px` }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddDoc} disabled={addSaving || !addForm.name}
            startIcon={addSaving ? <CircularProgress size={14} color="inherit" /> : <Add />}
            sx={primaryBtnSx}>
            {addSaving ? "Adding…" : "Add Document"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── TAB 3 — BGV ───────────────────────────────────────────────────────────────
function BgvTab({ employeeId, data, onRefresh, onSaveSuccess }) {
  const [form,    setForm]    = useState({});
  const [saving,  setSaving]  = useState(false);
  const [success, setSuccess] = useState(false);
  const [error,   setError]   = useState("");

  useEffect(() => {
    setForm({ bgv_status: data?.bgv_status || "Not Initiated", bgv_agency: data?.bgv_agency || "", bgv_remarks: data?.bgv_remarks || "" });
  }, [data]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true); setSuccess(false); setError("");
    try { await updateOnboarding(employeeId, form); onSaveSuccess?.("BGV details saved"); setSuccess(true); onRefresh(); setTimeout(() => setSuccess(false), 3000); }
    catch (err) { setError(err?.message || "Failed to save BGV details"); }
    finally { setSaving(false); }
  };

  const sc = BGV_COLOR[form.bgv_status] || BGV_COLOR["Not Initiated"];

  return (
    <Box>
      <SectionHeader title="Background Verification" subtitle="Track the BGV process for this employee" />
      <Box mb={3} p={2.5} sx={{ bgcolor: sc.bg, borderRadius: "12px", border: `1px solid ${sc.color}30`, display: "flex", alignItems: "center", gap: 1.5 }}>
        <VerifiedUser sx={{ color: sc.color, fontSize: 28 }} />
        <Box>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.overline, color: PURPLE_MUTED, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>Current BGV Status</Typography>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.section, fontWeight: 800, color: sc.color }}>{form.bgv_status}</Typography>
        </Box>
      </Box>
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6}>
          <TextField select fullWidth sx={fieldSx} size="small" label="BGV Status" value={form.bgv_status || ""} onChange={e => setForm(p => ({ ...p, bgv_status: e.target.value }))}>
            {BGV_STATUSES.map(s => <MenuItem key={s} value={s} >{s}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth sx={fieldSx} size="small" label="BGV Agency" value={form.bgv_agency || ""} onChange={e => setForm(p => ({ ...p, bgv_agency: e.target.value }))} placeholder="e.g. AuthBridge" />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth sx={fieldSx} multiline rows={4} size="small" label="BGV Remarks" value={form.bgv_remarks || ""} onChange={e => setForm(p => ({ ...p, bgv_remarks: e.target.value }))} placeholder="Notes about the verification process…" />
        </Grid>
      </Grid>
      <SaveBar onSave={handleSave} saving={saving} success={success} error={error} onDismissError={() => setError("")} />
    </Box>
  );
}

// ── TAB 4 — IT & ASSETS ───────────────────────────────────────────────────────
function ITAssetsTab({ employeeId, data, onRefresh, onSaveSuccess }) {
  const [form,    setForm]    = useState({});
  const [saving,  setSaving]  = useState(false);
  const [success, setSuccess] = useState(false);
  const [error,   setError]   = useState("");

  useEffect(() => {
    setForm({ laptop_serial: data?.laptop_serial || "", laptop_make_model: data?.laptop_make_model || "", access_card_number: data?.access_card_number || "", email_id_created: data?.email_id_created || "", it_notes: data?.it_notes || "" });
  }, [data]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true); setSuccess(false); setError("");
    try { await updateOnboarding(employeeId, form); setSuccess(true); onRefresh(); onSaveSuccess?.("IT & Asset details saved"); setTimeout(() => setSuccess(false), 3000); }
    catch (err) { setError(err?.message || "Failed to save IT & asset details"); }
    finally { setSaving(false); }
  };

  return (
    <Box>
      <SectionHeader title="IT & Asset Assignment" subtitle="Track hardware, access cards and system access" />
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6}><TextField fullWidth sx={fieldSx} size="small" label="Laptop Serial Number" value={form.laptop_serial || ""} onChange={e => setForm(p => ({ ...p, laptop_serial: e.target.value }))} placeholder="e.g. C02X123456" /></Grid>
        <Grid item xs={12} sm={6}><TextField fullWidth sx={fieldSx} size="small" label="Laptop Make & Model" value={form.laptop_make_model || ""} onChange={e => setForm(p => ({ ...p, laptop_make_model: e.target.value }))} placeholder="e.g. Apple MacBook Pro 14 M3" /></Grid>
        <Grid item xs={12} sm={6}><TextField fullWidth sx={fieldSx} size="small" label="Access Card Number" value={form.access_card_number || ""} onChange={e => setForm(p => ({ ...p, access_card_number: e.target.value }))} /></Grid>
        <Grid item xs={12} sm={6}><TextField fullWidth sx={fieldSx} size="small" label="Corporate Email ID" value={form.email_id_created || ""} onChange={e => setForm(p => ({ ...p, email_id_created: e.target.value }))} placeholder="e.g. name@zentreelabs.com" /></Grid>
        <Grid item xs={12}><TextField fullWidth sx={fieldSx} multiline rows={3} size="small" label="IT Notes" value={form.it_notes || ""} onChange={e => setForm(p => ({ ...p, it_notes: e.target.value }))} placeholder="System access, tools configured…" /></Grid>
      </Grid>
      <SaveBar onSave={handleSave} saving={saving} success={success} error={error} onDismissError={() => setError("")} />
    </Box>
  );
}

// ── TAB 5 — BANK & EMERGENCY ──────────────────────────────────────────────────
function BankEmergencyTab({ employeeId, data, onRefresh, onSaveSuccess }) {
  const [bank,    setBank]    = useState({});
  const [emg,     setEmg]     = useState({});
  const [saving,  setSaving]  = useState(false);
  const [success, setSuccess] = useState(false);
  const [error,   setError]   = useState("");

  useEffect(() => {
    setBank({ account_holder_name: data?.bank_details?.account_holder_name || "", account_number: data?.bank_details?.account_number || "", ifsc_code: data?.bank_details?.ifsc_code || "", bank_name: data?.bank_details?.bank_name || "", branch: data?.bank_details?.branch || "", account_type: data?.bank_details?.account_type || "Savings" });
    setEmg({ name: data?.emergency_contact?.name || "", relationship: data?.emergency_contact?.relationship || "", phone: data?.emergency_contact?.phone || "", email: data?.emergency_contact?.email || "", address: data?.emergency_contact?.address || "" });
  }, [data]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true); setSuccess(false); setError("");
    try { await updateOnboarding(employeeId, { bank_details: bank, emergency_contact: emg }); setSuccess(true); onRefresh(); onSaveSuccess?.("Bank & emergency contact saved"); setTimeout(() => setSuccess(false), 3000); }
    catch (err) { setError(err?.message || "Failed to save bank & emergency details"); }
    finally { setSaving(false); }
  };

  return (
    <Box>
      <SectionHeader title="Bank Account Details" subtitle="For payroll processing" />
      <Grid container spacing={2.5} mb={4}>
        <Grid item xs={12} sm={4}><TextField fullWidth sx={fieldSx} size="small" label="Account Holder Name" value={bank.account_holder_name || ""} onChange={e => setBank(p => ({ ...p, account_holder_name: e.target.value }))} /></Grid>
        <Grid item xs={12} sm={4}><TextField fullWidth sx={fieldSx} size="small" label="Account Number" value={bank.account_number || ""} onChange={e => setBank(p => ({ ...p, account_number: e.target.value }))} /></Grid>
        <Grid item xs={12} sm={4}><TextField fullWidth sx={fieldSx} size="small" label="IFSC Code" value={bank.ifsc_code || ""} onChange={e => setBank(p => ({ ...p, ifsc_code: e.target.value.toUpperCase() }))} placeholder="e.g. HDFC0001234" /></Grid>
        <Grid item xs={12} sm={4}><TextField fullWidth sx={fieldSx} size="small" label="Bank Name" value={bank.bank_name || ""} onChange={e => setBank(p => ({ ...p, bank_name: e.target.value }))} /></Grid>
        <Grid item xs={12} sm={4}><TextField fullWidth sx={fieldSx} size="small" label="Branch" value={bank.branch || ""} onChange={e => setBank(p => ({ ...p, branch: e.target.value }))} /></Grid>
        <Grid item xs={12} sm={4}>
          <TextField select fullWidth sx={fieldSx} size="small" label="Account Type" value={bank.account_type || "Savings"} onChange={e => setBank(p => ({ ...p, account_type: e.target.value }))}>
            {ACCOUNT_TYPES.map(t => <MenuItem key={t} value={t} >{t}</MenuItem>)}
          </TextField>
        </Grid>
      </Grid>
      <Divider sx={{ borderColor: PURPLE_LIGHT, mb: 3 }} />
      <SectionHeader title="Emergency Contact" subtitle="Person to contact in case of emergency" />
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6}><TextField fullWidth sx={fieldSx} size="small" label="Contact Name" value={emg.name || ""} onChange={e => setEmg(p => ({ ...p, name: e.target.value }))} /></Grid>
        <Grid item xs={12} sm={6}><TextField fullWidth sx={fieldSx} size="small" label="Relationship" value={emg.relationship || ""} onChange={e => setEmg(p => ({ ...p, relationship: e.target.value }))} placeholder="e.g. Spouse, Parent" /></Grid>
        <Grid item xs={12} sm={6}><TextField fullWidth sx={fieldSx} size="small" label="Phone" value={emg.phone || ""} onChange={e => setEmg(p => ({ ...p, phone: e.target.value }))} /></Grid>
        <Grid item xs={12} sm={6}><TextField fullWidth sx={fieldSx} size="small" label="Email" type="email" value={emg.email || ""} onChange={e => setEmg(p => ({ ...p, email: e.target.value }))} /></Grid>
        <Grid item xs={12}><TextField fullWidth sx={fieldSx} size="small" multiline rows={2} label="Address" value={emg.address || ""} onChange={e => setEmg(p => ({ ...p, address: e.target.value }))} /></Grid>
      </Grid>
      <SaveBar onSave={handleSave} saving={saving} success={success} error={error} onDismissError={() => setError("")} />
    </Box>
  );
}

// ── DETAIL DIALOG ─────────────────────────────────────────────────────────────
export function OnboardingDetail({ open, onClose, employee, onSaveSuccess }) {
  const [tab,     setTab]     = useState(0);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  const employeeId = employee?._id;

  const loadData = useCallback(async () => {
    if (!employeeId) { setLoading(false); setError("No employee selected"); return; }
    setLoading(true); setError("");
    try { const res = await getOnboarding(employeeId); setData(res?.data || null); }
    catch (err) { setError(err?.message || "Failed to load onboarding data"); }
    finally { setLoading(false); }
  }, [employeeId]);

  // Fresh load each time the dialog opens (never show the previous employee's data).
  useEffect(() => {
    if (open) { setData(null); setTab(0); loadData(); }
  }, [open, loadData]);

  const doneCount  = data?.checklist?.filter(i => i.done).length ?? 0;
  const totalCount = data?.checklist?.length ?? 0;
  const pct        = totalCount ? Math.round((doneCount / totalCount) * 100) : 0;

  const TABS = [
    { label: "Overview",         icon: <AssignmentInd sx={{ fontSize: 16 }} /> },
    { label: "Checklist",        icon: <CheckCircle   sx={{ fontSize: 16 }} /> },
    { label: "Documents",        icon: <Description   sx={{ fontSize: 16 }} /> },
    { label: "BGV",              icon: <VerifiedUser  sx={{ fontSize: 16 }} /> },
    { label: "IT & Assets",      icon: <Laptop        sx={{ fontSize: 16 }} /> },
    { label: "Bank & Emergency", icon: <AccountBalance sx={{ fontSize: 16 }} /> },
  ];

  return (
    <ThemeProvider theme={recruiterTheme}>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { minHeight: "85vh", borderRadius: `${RADIUS.lg}px` } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ px: 3, pt: 2, pb: 0, background: "linear-gradient(135deg, #1a0533 0%, #2D1B5E 60%, #4F378B 100%)" }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} gap={2}>
            <Box display="flex" alignItems="center" gap={2} minWidth={0}>
              <Avatar sx={{ width: 44, height: 44, bgcolor: "rgba(255,255,255,0.15)", border: "2px solid rgba(255,255,255,0.25)", fontSize: TYPE.cardTitle, fontWeight: 700, color: "#fff" }}>
                {(employee?.name || "?")[0].toUpperCase()}
              </Avatar>
              <Box minWidth={0}>
                <Typography title={employee?.name} sx={{ fontFamily: FONT_DISPLAY, color: "#fff", fontWeight: 700, fontSize: TYPE.section, lineHeight: 1.25, ...ELLIPSIS }}>{employee?.name || employeeId || "Onboarding"}</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography sx={{ fontFamily: FONT_BODY, color: "rgba(255,255,255,0.7)", fontSize: TYPE.caption }}>
                    {[employee?.emp_id, employee?.designation || "Employee"].filter(Boolean).join(" · ")}
                  </Typography>
                  {data && (
                    <Chip label={`${doneCount}/${totalCount} checklist`} size="small" sx={{
                      height: 20, fontWeight: 700,
                      bgcolor: pct === 100 ? "rgba(22,163,74,0.3)" : "rgba(255,255,255,0.15)",
                      color: pct === 100 ? "#86efac" : "rgba(255,255,255,0.8)",
                    }} />
                  )}
                </Box>
              </Box>
            </Box>
            <IconButton size="small" onClick={onClose} aria-label="Close" sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.1)" } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Tabs value={tab} onChange={(_, v) => setTab(v)}
            variant="scrollable" scrollButtons="auto"
            TabIndicatorProps={{ style: { backgroundColor: "#B794F4", height: 3 } }}
            sx={{
              "& .MuiTab-root": {
                color: "rgba(255,255,255,0.6)", minHeight: 44, px: 2,
              },
              "& .Mui-selected": { color: "#fff !important" },
            }}>
            {TABS.map((t, i) => (
              <Tab key={i} label={t.label} icon={t.icon} iconPosition="start" sx={{ gap: 0.5 }} />
            ))}
          </Tabs>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: COLORS.surfaceTint }}>
        {loading && !data && (
          <Box display="flex" flexDirection="column" alignItems="center" py={8} gap={1.5}>
            <CircularProgress size={32} sx={{ color: PURPLE_BRAND }} />
            <Typography sx={{ fontSize: TYPE.small, color: PURPLE_MUTED }}>Loading onboarding details…</Typography>
          </Box>
        )}
        {!loading && error && !data && (
          <Alert severity="error" sx={{ mt: 3, borderRadius: `${RADIUS.sm}px` }}
            action={employeeId ? <Button color="inherit" size="small" onClick={loadData} sx={{ textTransform: "none", fontWeight: 600 }}>Retry</Button> : null}>
            {error}
          </Alert>
        )}
        {data && (
          <Box sx={{ mt: 3, bgcolor: COLORS.surface, borderRadius: `${RADIUS.md}px`, border: `1px solid ${PURPLE_LIGHT}`, p: 3 }}>
            {error && <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2, borderRadius: `${RADIUS.sm}px` }}>{error}</Alert>}
            {tab === 0 && <OverviewTab      employeeId={employeeId} data={data} employee={employee} onRefresh={loadData} onSaveSuccess={onSaveSuccess} />}
            {tab === 1 && <ChecklistTab     employeeId={employeeId} data={data} onRefresh={loadData} onSaveSuccess={onSaveSuccess} />}
            {tab === 2 && <DocumentsTab     employeeId={employeeId} data={data} onRefresh={loadData} onSaveSuccess={onSaveSuccess} />}
            {tab === 3 && <BgvTab           employeeId={employeeId} data={data} onRefresh={loadData} onSaveSuccess={onSaveSuccess} />}
            {tab === 4 && <ITAssetsTab      employeeId={employeeId} data={data} onRefresh={loadData} onSaveSuccess={onSaveSuccess} />}
            {tab === 5 && <BankEmergencyTab employeeId={employeeId} data={data} onRefresh={loadData} onSaveSuccess={onSaveSuccess} />}
          </Box>
        )}
      </DialogContent>
    </Dialog>
    </ThemeProvider>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────
const headCellSx = {
  fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: PURPLE_MUTED,
  textTransform: "uppercase", letterSpacing: "0.08em", py: 1.5,
  borderBottom: `1px solid ${PURPLE_LIGHT}`, whiteSpace: "nowrap",
};
const cardSx = {
  bgcolor: COLORS.surface, border: `1px solid ${PURPLE_LIGHT}`, borderRadius: `${RADIUS.lg}px`,
  overflow: "hidden", boxShadow: SHADOW.card,
};

export default function OnboardingPage({ onSaveSuccess }) {
  // Own canvas + page padding only on the standalone /onboarding route.
  const { pathname } = useLocation();
  const standalone = pathname.toLowerCase().startsWith("/onboarding");

  const [employees,  setEmployees]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");
  const [search,     setSearch]     = useState("");
  const [bgvFilter,  setBgvFilter]  = useState("");
  const [selected,   setSelected]   = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [onbData,    setOnbData]    = useState({});
  const [snack,      setSnack]      = useState("");

  const loadEmployees = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res  = await getEmployees();
      const emps = Array.isArray(res?.data) ? res.data : [];
      setEmployees(emps);
      const summaries = await Promise.allSettled(emps.map(e => getOnboarding(e._id)));
      const map = {};
      summaries.forEach((r, i) => { if (r.status === "fulfilled") map[emps[i]._id] = r.value?.data; });
      setOnbData(map);
    } catch (err) { setError(err?.message || "Failed to load employees"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  const openDetail = (emp) => { setSelected(emp); setDetailOpen(true); };
  // Refresh the list so KPIs and rows reflect edits made in the dialog.
  const closeDetail = () => { setDetailOpen(false); loadEmployees(); };
  // Inside the HR dashboard the dashboard snackbar is used; standalone, our own.
  const handleSaveSuccess = onSaveSuccess || setSnack;

  const filtered = employees.filter(e => {
    const q    = search.trim().toLowerCase();
    const mQ   = !q || e.name?.toLowerCase().includes(q) || String(e.emp_id ?? "").toLowerCase().includes(q);
    const ob   = onbData[e._id];
    const mBgv = !bgvFilter || (ob?.bgv_status || "Not Initiated") === bgvFilter;
    return mQ && mBgv;
  });

  const totalOnboarding = employees.length;
  const bgvCompleted    = Object.values(onbData).filter(d => d?.bgv_status === "Completed").length;
  const docsPending     = Object.values(onbData).reduce((acc, d) => acc + (d?.documents?.filter(doc => doc.status === "Pending").length || 0), 0);
  const checklistFull   = Object.values(onbData).filter(d => { const items = d?.checklist || []; return items.length > 0 && items.every(i => i.done); }).length;

  const body = (
    <Box sx={{ fontFamily: FONT_BODY }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={3}>
        <Box>
          <Typography sx={{
            fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.page, lineHeight: 1.25, letterSpacing: "-0.02em",
            background: "linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
          }}>
            Onboarding
          </Typography>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.body, color: PURPLE_MUTED, fontWeight: 500, mt: 0.5 }}>
            Manage employee onboarding, documents, BGV and asset allocation
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={loadEmployees} disabled={loading} size="small" aria-label="Refresh" sx={{
              width: CONTROL_HEIGHT.md, height: CONTROL_HEIGHT.md,
              border: `1px solid ${PURPLE_LIGHT}`, borderRadius: `${RADIUS.sm}px`, bgcolor: COLORS.surface, color: PURPLE_MUTED,
              "&:hover": { bgcolor: PURPLE_SOFT, borderColor: PURPLE_BRAND, color: PURPLE_BRAND },
            }}>
              <Refresh fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: "Total Employees",    value: totalOnboarding, accent: PURPLE_BRAND, icon: <People sx={{ fontSize: 20 }} /> },
          { label: "BGV Completed",      value: bgvCompleted,    accent: "#059669",    icon: <VerifiedUser sx={{ fontSize: 20 }} /> },
          { label: "Docs Pending",       value: docsPending,     accent: "#d97706",    icon: <Description sx={{ fontSize: 20 }} /> },
          { label: "Checklist Complete", value: checklistFull,   accent: "#1d4ed8",    icon: <CheckCircle sx={{ fontSize: 20 }} /> },
        ].map(({ label, value, accent, icon }) => (
          <Grid item xs={6} md={3} key={label}>
            <Box sx={{
              p: 2, borderRadius: `${RADIUS.md}px`, bgcolor: COLORS.surface, border: `1px solid ${PURPLE_LIGHT}`, height: "100%",
              display: "flex", alignItems: "flex-start", gap: 2,
              position: "relative", overflow: "hidden",
              boxShadow: SHADOW.card,
              "&:hover": { boxShadow: SHADOW.cardHover },
              transition: "box-shadow 0.2s",
              "&::before": { content: '""', position: "absolute", top: 0, left: 0, right: 0, height: 3, bgcolor: accent },
            }}>
              <Box sx={{ p: 1, bgcolor: `${accent}18`, borderRadius: `${RADIUS.sm}px`, color: accent, display: "flex", flexShrink: 0 }}>{icon}</Box>
              <Box flex={1} minWidth={0}>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: PURPLE_MUTED, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5 }}>{label}</Typography>
                {loading
                  ? <CircularProgress size={20} sx={{ color: accent }} />
                  : <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.page, fontWeight: 800, color: PURPLE_DEEP, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
                }
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Employees card: filters + table */}
      <Box sx={cardSx}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", px: 3, py: 2, borderBottom: `1px solid ${PURPLE_LIGHT}` }}>
          <TextField
            placeholder="Search by name or employee ID…"
            value={search} onChange={e => setSearch(e.target.value)}
            size="small" sx={{ flexGrow: 1, minWidth: 220, ...fieldSx }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: PURPLE_PALE }} /></InputAdornment> }}
          />
          <TextField select value={bgvFilter} onChange={e => setBgvFilter(e.target.value)}
            size="small" sx={{ minWidth: 200, ...fieldSx }} label="BGV Status">
            <MenuItem value="">All BGV statuses</MenuItem>
            {BGV_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
        </Box>

        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={10} gap={1.5}>
            <CircularProgress size={32} sx={{ color: PURPLE_BRAND }} />
            <Typography sx={{ fontSize: TYPE.small, color: PURPLE_MUTED }}>Loading employees…</Typography>
          </Box>
        ) : error ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={8} gap={1.5}>
            <Typography sx={{ fontSize: TYPE.body, fontWeight: 600, color: COLORS.error }}>{error}</Typography>
            <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={loadEmployees}
              sx={{ height: CONTROL_HEIGHT.sm, textTransform: "none", fontWeight: 600, borderRadius: `${RADIUS.sm}px` }}>
              Retry
            </Button>
          </Box>
        ) : employees.length === 0 ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <Box sx={{ width: 56, height: 56, borderRadius: "50%", bgcolor: PURPLE_SOFT, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
              <AssignmentInd sx={{ fontSize: 28, color: PURPLE_PALE }} />
            </Box>
            <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.body, color: PURPLE_DEEP, mb: 0.5 }}>No employees found</Typography>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, color: PURPLE_MUTED }}>Add employees first to manage their onboarding.</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: COLORS.surfaceTint }}>
                    {["Employee", "Joining Date", "Checklist", "Documents", "BGV Status", "Actions"].map(h => (
                      <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6, fontSize: TYPE.small, color: PURPLE_MUTED }}>No employees match your filters</TableCell>
                    </TableRow>
                  ) : filtered.map(emp => {
                    const ob          = onbData[emp._id] || {};
                    const checklist   = ob.checklist || [];
                    const done        = checklist.filter(i => i.done).length;
                    const pct         = checklist.length ? Math.round((done / checklist.length) * 100) : 0;
                    const docs        = ob.documents || [];
                    const pendingDocs = docs.filter(d => d.status === "Pending").length;
                    const bgvStatus   = ob.bgv_status || "Not Initiated";
                    const bgvSc       = BGV_COLOR[bgvStatus] || BGV_COLOR["Not Initiated"];

                    return (
                      <TableRow key={emp._id} hover sx={{ "&:last-child td": { borderBottom: 0 }, "& td": { borderBottom: `1px solid ${PURPLE_LIGHT}`, py: 1.5 } }}>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                            <Avatar sx={{ width: 32, height: 32, fontSize: TYPE.small, fontWeight: 700, background: "linear-gradient(135deg, #2D1B5E, #6750A4)" }}>
                              {(emp.name || "?")[0].toUpperCase()}
                            </Avatar>
                            <Box minWidth={0}>
                              <Typography title={emp.name} sx={{ fontSize: TYPE.small, fontWeight: 700, color: PURPLE_DEEP, ...ELLIPSIS }}>{emp.name || "—"}</Typography>
                              <Typography sx={{ fontSize: TYPE.caption, color: PURPLE_MUTED, whiteSpace: "nowrap" }}>{emp.emp_id || "—"}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: TYPE.small, color: PURPLE_DEEP, whiteSpace: "nowrap" }}>{fmtDate(ob.joining_date || emp.date_of_joining)}</TableCell>
                        <TableCell sx={{ minWidth: 140 }}>
                          <Box>
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                              <Typography sx={{ fontSize: TYPE.caption, color: PURPLE_MUTED, fontVariantNumeric: "tabular-nums" }}>{done}/{checklist.length}</Typography>
                              <Typography sx={{ fontSize: TYPE.caption, fontWeight: 700, color: pct === 100 ? "#166534" : PURPLE_BRAND, fontVariantNumeric: "tabular-nums" }}>{pct}%</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3, bgcolor: PURPLE_LIGHT, "& .MuiLinearProgress-bar": { background: pct === 100 ? "#16a34a" : "linear-gradient(90deg, #6750A4, #4F378B)", borderRadius: 3 } }} />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Chip label={`${docs.length} docs`} size="small" sx={{ height: 22, bgcolor: PURPLE_SOFT, color: PURPLE_MUTED }} />
                            {pendingDocs > 0 && <Chip label={`${pendingDocs} pending`} size="small" sx={{ height: 22, bgcolor: "#fef9c3", color: "#854d0e", fontWeight: 700 }} />}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Chip label={humanizeStatus(bgvStatus)} size="small" sx={{ fontWeight: 700, height: 22, bgcolor: bgvSc.bg, color: bgvSc.color, border: `1px solid ${bgvSc.color}30`, borderRadius: `${RADIUS.pill}px` }} />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Manage onboarding">
                            <IconButton size="small" onClick={() => openDetail(emp)} aria-label="Manage onboarding" sx={{ color: PURPLE_BRAND, bgcolor: PURPLE_SOFT, "&:hover": { bgcolor: PURPLE_LIGHT }, borderRadius: `${RADIUS.sm}px` }}>
                              <Visibility sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            {filtered.length > 0 && (
              <Box sx={{ px: 3, py: 1.5, borderTop: `1px solid ${PURPLE_LIGHT}`, bgcolor: COLORS.surfaceTint }}>
                <Typography sx={{ fontSize: TYPE.caption, color: PURPLE_MUTED }}>
                  Showing <strong style={{ color: PURPLE_DEEP }}>{filtered.length}</strong> of <strong style={{ color: PURPLE_DEEP }}>{employees.length}</strong> employees
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      <OnboardingDetail open={detailOpen} onClose={closeDetail} employee={selected} onSaveSuccess={handleSaveSuccess} />

      {!onSaveSuccess && (
        <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack("")}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert onClose={() => setSnack("")} severity="success" variant="filled"
            sx={{ borderRadius: `${RADIUS.sm}px`, fontWeight: 600, bgcolor: PURPLE_BRAND }}>
            {snack}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );

  return (
    <ThemeProvider theme={recruiterTheme}>
      {standalone ? (
        <Box sx={{ bgcolor: COLORS.background, minHeight: "100vh", p: `${PAGE_PADDING}px` }}>{body}</Box>
      ) : body}
    </ThemeProvider>
  );
}
